import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash/get";
import { trackCtaClick, trackPodcastInteraction } from "../../../ga-utils";
import { UPDATE_PODCAST_PLAYER } from "../../store/actions";

import "./podcastwidget.m.css";
import { isDesktop } from "react-device-detect";
import { Link } from "@quintype/components";

const initialPodcastPlayerState = {
  podcastData: null,
  isPlaying: false,
  isClosebutton: false,
  isPlayingButton: false,
  currentTime: 0,
  duration: 0,
  publishDate: "",
  isLoading: false,
  error: "",
};

let sharedAudioInstance = null;

const getSharedAudio = () => {
  if (typeof window === "undefined") return null;

  if (!sharedAudioInstance) {
    sharedAudioInstance = new Audio();
    sharedAudioInstance.preload = "metadata";
  }

  return sharedAudioInstance;
};

const getPodcastEventLabel = ({ surface, isTopnewWidget, isHomePage }) => {
  if (surface === "bottomplayer_close") return "bottomplayer_close";

  if (isHomePage) {
    if (surface === "bottomplayer") return "home_bottomplayer";
    return isTopnewWidget ? "home_smallcomponent" : "home_bigcomponent";
  }

  return surface === "bottomplayer" ? "landing_bottomplayer" : "landing_disc";
};

const hasValidPodcastData = (data) =>
  !!(
    data &&
    typeof data === "object" &&
    typeof data.url === "string" &&
    data.url.trim() &&
    typeof data.title === "string" &&
    data.title.trim()
  );

const PodcastWidget = ({ isTopnewWidget = false, onAvailabilityChange }) => {
  const dispatch = useDispatch();
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], {}));
  const isHomePage = pageType === "home-page";
  const qtState = useSelector((state) => state);
  const qtConfig = get(qtState, ["qt", "config"], {});
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";
  const {
    podcastData,
    isPlaying,
    isClosebutton,
    isPlayingButton,
    currentTime,
    duration,
    publishDate,
    isLoading,
    error,
  } = useSelector((state) => get(state, ["podcastPlayer"], initialPodcastPlayerState));
  // const isPodcastApiurl = get(collection, ["associated-metadata", "podcast_apiurl"], false);
  const isPodcastApiurl = get(qtConfig, ["publisher-attributes", "podcast-apiurl"], false);
  // 1. Fetch data on component mount
  useEffect(() => {
    if (hasValidPodcastData(podcastData)) {
      if (typeof onAvailabilityChange === "function") {
        onAvailabilityChange(true);
      }
      return;
    }

    if (typeof isPodcastApiurl !== "string" || !isPodcastApiurl.trim()) {
      if (typeof onAvailabilityChange === "function") {
        onAvailabilityChange(false);
      }

      dispatch({
        type: UPDATE_PODCAST_PLAYER,
        payload: {
          podcastData: null,
          publishDate: "",
          isLoading: false,
          error: "Podcast is unavailable right now.",
        },
      });
      return;
    }

    let isMounted = true;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;

    dispatch({
      type: UPDATE_PODCAST_PLAYER,
      payload: {
        isLoading: true,
        error: "",
      },
    });

    fetch(isPodcastApiurl, controller ? { signal: controller.signal } : undefined)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Podcast request failed with status ${response.status}`);
        }

        return response.json();
      })
      .then((data) => {
        if (!isMounted) return;

        if (!hasValidPodcastData(data)) {
          throw new Error("Podcast response did not include the required fields.");
        }

        const timestamp = data.last_published_at;
        const formattedDate = timestamp
          ? new Date(timestamp * 1000).toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })
          : "";

        dispatch({
          type: UPDATE_PODCAST_PLAYER,
          payload: {
            podcastData: data,
            publishDate: formattedDate,
            isLoading: false,
            error: "",
          },
        });

        if (typeof onAvailabilityChange === "function") {
          onAvailabilityChange(true);
        }
      })
      .catch((fetchError) => {
        if (!isMounted || fetchError?.name === "AbortError") return;

        dispatch({
          type: UPDATE_PODCAST_PLAYER,
          payload: {
            podcastData: null,
            publishDate: "",
            isLoading: false,
            error: "Unable to load the podcast right now.",
          },
        });

        if (typeof onAvailabilityChange === "function") {
          onAvailabilityChange(false);
        }
      });

    return () => {
      isMounted = false;
      if (controller) controller.abort();
    };
  }, [dispatch, isPodcastApiurl, onAvailabilityChange, podcastData]);

  useEffect(() => {
    const audio = getSharedAudio();
    if (!audio || !podcastData?.url) return;

    if (audio.src !== podcastData.url) {
      audio.src = podcastData.url;
      audio.load();
    }
  }, [podcastData]);

  useEffect(() => {
    const audio = getSharedAudio();
    if (!audio) return;

    const syncPlayerState = (payload) => {
      dispatch({
        type: UPDATE_PODCAST_PLAYER,
        payload,
      });
    };

    const handleTimeUpdate = () => {
      syncPlayerState({ currentTime: audio.currentTime });
    };

    const handleLoadedMetadata = () => {
      syncPlayerState({ duration: audio.duration || 0 });
    };

    const handlePlay = () => {
      syncPlayerState({
        isPlaying: true,
        isClosebutton: true,
        isPlayingButton: true,
      });
    };

    const handlePause = () => {
      syncPlayerState({ isPlayingButton: false });
    };

    const handleEnded = () => {
      syncPlayerState({
        currentTime: 0,
        isPlaying: false,
        isClosebutton: false,
        isPlayingButton: false,
      });
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    handleLoadedMetadata();
    handleTimeUpdate();

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [dispatch]);

  // 2. Helper to format time
  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 5 ? "0" + seconds : seconds}`;
  };
  // 3. Handlers
  const togglePlay = () => {
    const audio = getSharedAudio();
    if (!audio || !podcastData?.url) return;

    if (audio.src !== podcastData.url) {
      audio.src = podcastData.url;
      audio.load();
    }

    if (audio.paused) {
      audio.play().catch(() => {
        dispatch({
          type: UPDATE_PODCAST_PLAYER,
          payload: {
            isPlaying: false,
            isClosebutton: false,
            isPlayingButton: false,
          },
        });
      });
    } else {
      audio.pause();
      dispatch({
        type: UPDATE_PODCAST_PLAYER,
        payload: {
          isPlayingButton: false,
        },
      });
    }
  };

  const progressBar = duration ? (currentTime / duration) * 100 : 0;

  const skip = (amount) => {
    const audio = getSharedAudio();
    if (!audio) return;

    const nextTime = Math.min(Math.max(audio.currentTime + amount, 0), audio.duration || 0);
    audio.currentTime = nextTime;
    dispatch({
      type: UPDATE_PODCAST_PLAYER,
      payload: {
        currentTime: nextTime,
      },
    });
  };

  const handleProgressChange = (e) => {
    const audio = getSharedAudio();
    if (!audio) return;

    const newTime = e.target.value;
    audio.currentTime = Number(newTime);
    dispatch({
      type: UPDATE_PODCAST_PLAYER,
      payload: {
        currentTime: Number(newTime),
      },
    });
  };

  if (isLoading || error || !hasValidPodcastData(podcastData)) return null;

  const trackPlayClick = () => {
    if (typeof window !== "undefined") {
      trackCtaClick({
        linkUrl: podcastData.url,
        content_id: '',
        linkText: podcastData.title,
        pageType: pageType,
        location: window.location,
      });
    }
  };

  const trackPodcastEvent = (eventAction, eventLabel) => {
    if (typeof window !== "undefined") {
      trackPodcastInteraction({
        eventAction,
        eventLabel,
        pageType,
        location: window.location,
        config: qtConfig,
      });
    }
  };

  const handleTogglePlay = (eventLabel) => {
    const audio = getSharedAudio();
    const eventAction = audio?.paused ? "play" : "pause";

    trackPlayClick();
    trackPodcastEvent(eventAction, eventLabel);
    togglePlay();
  };

  const handleSkip = (amount, eventLabel) => {
    trackPodcastEvent(amount < 0 ? "rewind" : "forward", eventLabel);
    skip(amount);
  };

  const handleCloseStickyPlayer = () => {
    const audio = getSharedAudio();

    trackPodcastEvent("bottomplayer_close", "home_bottomplayer");
    if (audio && !audio.paused) {
      audio.pause();
    }
    dispatch({
      type: UPDATE_PODCAST_PLAYER,
      payload: {
        isPlaying: false,
        isClosebutton: false,
        isPlayingButton: false,
      },
    });
  };

  return (
    <>
      <div styleName={`podcast-panel ${isTopnewWidget ? "podcast-topnews" : ""}`}>
        <div styleName="podcast-panel-inner">
          <div styleName="podcast-image">
            {isDesktop ? (
              <img
                src="https://media.openthemagazine.com/openthemagazine/2026-05-06/01ooxr8t/mic-img-desktop.png"
                alt="podcast"
                title="podcast"
                width="346"
                height="797"
                loading="lazy"
              />
            ) : (
              <img
                src="https://media.openthemagazine.com/openthemagazine/2026-05-06/3pqbxx5n/mic-img-mobile.png"
                alt="podcast"
                title="podcast"
                width="346"
                height="797"
                loading="lazy"
              />
            )}

            {isDesktop && (
              <div styleName="player-bg-img">
                <div styleName="disc-play"></div>
                <button
                  styleName={`disc-play-button ${isPlayingButton ? "pause" : ""}`}
                  onClick={() => {
                    handleTogglePlay(
                      getPodcastEventLabel({
                        surface: "disc",
                        isTopnewWidget,
                        isHomePage,
                      })
                    );
                  }}
                >
                  Play
                </button>
              </div>
            )}
          </div>
          {!isDesktop && (
            <div styleName="player-bg-img">
              <div styleName="disc-play"></div>
              <button
                styleName={`disc-play-button ${isPlayingButton ? "pause" : ""}`}
                onClick={() => {
                  handleTogglePlay(
                    getPodcastEventLabel({
                      surface: "disc",
                      isTopnewWidget,
                      isHomePage,
                    })
                  );
                }}
              >
                Play
              </button>
            </div>
          )}
        </div>
        <div styleName="podcast-audio">
          <div styleName="podcast-description">
            <Link href={`/open-offbeat-podcast`} aria-label="podcast widget" disableAjaxLinks={true}>
              <span styleName="sub-heading" id="publishDate">
                {publishDate}
              </span>
              <div styleName="main-heading">OPEN <br/>OFFBEAT</div>
              {!isDesktop && isTopnewWidget && <p styleName="podcast-info">Every weekday at 7 PM. Stay tuned!</p>}
              {isDesktop && !isTopnewWidget && (
                <p styleName="podcast-summary isDesktop">
                  Smart. Strange. Surprising. OFFBEAT wraps up the most intriguing, unusual, and talked-about moments you didn't see coming through the day. This audio-podcast will make you pause, smile and think. Watch this space every weekday at 7pm to slide into stories beyond the run-of-the-mill headlines.
                </p>
              )}
            </Link>
          </div>
        </div>
      </div>
      <div
        styleName={`sticky-podcast-box ${isPlaying && isClosebutton ? "show" : ""} ${isAdfreePage ? "adfree-sticky-podcast" : ""}`}
      >
        <div className="container">
          <div styleName="box1">
            <div styleName="podcast-fortune-logo">
              <span styleName="mY42F"></span>
            </div>
            <div>
              <div styleName="podcast-mini-heading">OPEN OFFBEAT</div>
              <small styleName="timestamp" id="publishDatemini">
                {publishDate}
              </small>
            </div>
          </div>
          <div styleName="podcast-cta">
            <div styleName="player-btns">
              <button
                styleName="back-btn"
                onClick={() =>
                  handleSkip(
                    -5,
                    getPodcastEventLabel({
                      surface: "bottomplayer",
                      isTopnewWidget,
                      isHomePage,
                    })
                  )
                }
              >
                5
              </button>
              <div styleName="playbtnbox">
                <button
                  styleName={`play-button ${isPlayingButton ? "pause" : ""}`}
                  onClick={() => {
                    handleTogglePlay(
                      getPodcastEventLabel({
                        surface: "bottomplayer",
                        isTopnewWidget,
                        isHomePage,
                      })
                    );
                  }}
                >
                  Play
                </button>
              </div>
              <button
                styleName="forward-btn"
                onClick={() =>
                  handleSkip(
                    5,
                    getPodcastEventLabel({
                      surface: "bottomplayer",
                      isTopnewWidget,
                      isHomePage,
                    })
                  )
                }
              >
                5
              </button>
            </div>

            <div styleName="time-display">
              <span styleName="currentTime">{formatTime(currentTime)}</span>
              <div styleName="seek-container">
                <div styleName="seek-bg">
                  <div styleName="seek-fill" style={{ width: progressBar + "%" }}></div>
                </div>
                <input
                  type="range"
                  min="0"
                  styleName="progress-bar"
                  max={duration || 0}
                  value={currentTime}
                  onChange={handleProgressChange}
                  style={{ display: "none" }}
                />
              </div>
              <span styleName="duration">{formatTime(duration)}</span>
            </div>
          </div>
          <button
            aria-label="close sticky podcast"
            styleName="close-sticky-podcast"
            onClick={handleCloseStickyPlayer}
          ></button>
        </div>
      </div>
    </>
  );
};

PodcastWidget.propTypes = {
  isTopnewWidget: PropTypes.bool,
  onAvailabilityChange: PropTypes.func,
};

export default PodcastWidget;
